// collision.js should export boxesOverlap(x1,y1,x2,y2,w2,h2)
import { boxesOverlap } from "./collision";

const FRAME_WIDTH = 32;
const FRAME_HEIGHT = 32;

/**
 * Spawn `count` Pokemon at random positions, leaving
 * room for a single-frame sprite of FRAME_WIDTH×FRAME_HEIGHT.
 */
export function spawnRandomPokemon(count, allSprites, mapWidth, mapHeight) {
  if (!allSprites || allSprites.length === 0) return [];

  const newMons = [];
  for (let i = 0; i < count; i++) {
    const { sprite, species } =
      allSprites[Math.floor(Math.random() * allSprites.length)];

    // leave room for a 32×32 sprite
    const x = Math.floor(Math.random() * (mapWidth - FRAME_WIDTH));
    const y = Math.floor(Math.random() * (mapHeight - FRAME_HEIGHT));

    const dirs = ["up", "left", "down", "right"];
    const direction = dirs[Math.floor(Math.random() * dirs.length)];

    newMons.push({
      id: i,
      sprite,
      species,
      x,
      y,
      direction,
      step: 0,
    });
  }
  return newMons;
}

/**
 * Let each Pokémon wander one tile (or skip), checking collisions.
 *
 * @param {Array} prevState       — array of mons from spawnRandomPokemon or previous wander
 * @param {number} tileSize       — grid size to move each step
 * @param {number} mapWidth       — full map pixel width
 * @param {number} mapHeight      — full map pixel height
 * @param {{x:number,y:number}} playerPos
 * @param {Function} isCollidingWithWalls(x,y) => boolean
 */
export function wanderPokemons(
  prevState,
  tileSize,
  mapWidth,
  mapHeight,
  playerPos,
  isCollidingWithWalls
) {
  return prevState.map((mon, index, allPokemons) => {
    // 50% chance to skip moving
    if (Math.random() < 0.5) return mon;

    const dirs = ["up", "left", "down", "right"];
    const direction = dirs[Math.floor(Math.random() * dirs.length)];

    let newX = mon.x;
    let newY = mon.y;

    // attempt move
    switch (direction) {
      case "up":
        newY -= tileSize;
        break;
      case "left":
        newX -= tileSize;
        break;
      case "down":
        newY += tileSize;
        break;
      case "right":
        newX += tileSize;
        break;
      default:
        // no-op
        break;
    }

    // clamp into bounds
    const maxX = mapWidth - FRAME_WIDTH;
    const maxY = mapHeight - FRAME_HEIGHT;
    newX = Math.max(0, Math.min(newX, maxX));
    newY = Math.max(0, Math.min(newY, maxY));

    // small hit‐box for collisions
    const pokemonBox = { x: newX, y: newY, width: 16, height: 16 };

    // 1) walls
    if (isCollidingWithWalls(newX, newY)) {
      return { ...mon, direction };
    }

    // 2) player
    const playerBox = {
      x: playerPos.x,
      y: playerPos.y,
      width: 32,
      height: 32,
    };
    if (
      boxesOverlap(
        pokemonBox.x,
        pokemonBox.y,
        playerBox.x,
        playerBox.y,
        playerBox.width,
        playerBox.height,
        pokemonBox.width,
        pokemonBox.height
      )
    ) {
      return { ...mon, direction };
    }

    // 3) other Pokémon
    const hitOther = allPokemons.some((other, idx) => {
      if (idx === index) return false;
      return boxesOverlap(
        pokemonBox.x,
        pokemonBox.y,
        other.x,
        other.y,
        16,
        16,
        16,
        16
      );
    });
    if (hitOther) {
      return { ...mon, direction };
    }

    // apply move + advance frame (0–3)
    const step = (mon.step + 1) % 4;
    return {
      ...mon,
      x: newX,
      y: newY,
      direction,
      step,
    };
  });
}

/**
 * Given a direction and step (0–3), return the
 * x/y pixel offset into a 4×4 grid of 32×32 frames.
 */
export function getPokemonFrameOffset(direction, step) {
  // assign rows: down=0, left=1, right=2, up=3
  const rowMap = {
    down: 0,
    left: 1,
    right: 2,
    up: 3,
  };
  const row = rowMap[direction] || 0;
  return {
    x: step * FRAME_WIDTH,
    y: row * FRAME_HEIGHT,
  };
}
